import React from "react";
import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import Skeleton from "components/Skeleton";
import EmptyState from "components/EmptyState";
import { parseSort, toggleSort } from "utilities/listSort";


/**
 * RecordTable — the shared list-view table for the CRM.
 *
 * Deliberately DUMB: no fetching, no filtering, no derivation. It takes rows and
 * a column model and renders one of four states. Everything it knows, it was
 * handed. That keeps the shared surface small enough that "does it render" is
 * most of the verification, which matters because sound-app has no
 * component-test harness.
 *
 * WHY A COLUMN MODEL rather than hand-written <thead>/<tbody>:
 * audience-gated columns were the live hazard. MidPortfolio hides `Source` from
 * processor users (SOUND-570 — processors must never see which partner sourced a
 * merchant), and it did so by gating the <th> in one place and the <td> in
 * another, with a comment begging the next reader to keep the two in sync. One
 * entry here defines both, so a gated column cannot desynchronise its header
 * from its cells. The bug class is gone by construction, not by comment.
 *
 * The four states, in the order they're checked:
 *   loading           → a table-shaped skeleton, never a bare shell
 *   error             → message + Retry (the pages that lack this render a
 *                       failed load as "no results", which is indistinguishable
 *                       from an empty portfolio)
 *   empty + filtered  → "no matching X — adjust your filters"
 *   empty             → EmptyState, with an action if the page offers one
 */

/**
 * TYPE SCALE lives here, not in the column definitions.
 *
 * The first cut let each column set its own size — some cells declared `text-sm`
 * and some inherited the 16px base — so the same row rendered at two sizes and
 * the table read as a form rather than a dataset. These are scanning surfaces
 * that will carry thousands of rows, so the model is Jira's: one uniform, small
 * size for every cell, tight row rhythm, and emphasis carried by WEIGHT and
 * COLOR only. A column may set color, weight or truncation; it must not set
 * size. Nothing enforces that but this comment and the fact that cells no longer
 * need to.
 */
// `align-middle` is load-bearing, not cosmetic. Table cells default to
// vertical-align: BASELINE, and an inline-flex pill derives its baseline from
// its first flex item — a 6px status dot for TicketStatusPill, a 12px warning
// glyph for SlaBadge. Different derived baselines put two pills in the same row
// at visibly different heights, and no amount of matching their padding fixes
// it. Centering each cell in the row makes the row the reference instead.
const CELL_BASE = "px-4 py-2 align-middle text-[13px] leading-5 text-foreground";

/**
 * Truncation is the default, but a column can opt out with `truncate: false`.
 *
 * It has to be opt-out-able because `truncate` sets overflow:hidden, which CLIPS
 * anything a cell positions outside its own box — the /tickets dependency marker
 * renders a hover popover from inside its cell, and it would simply vanish.
 * Columns that opt out are responsible for not overflowing the row.
 */
const cellClass = (col) =>
  col.truncate === false ? CELL_BASE : `${CELL_BASE} truncate`;

/**
 * WIDTHS + TRUNCATION.
 *
 * The table is `table-fixed` and every column declares a `width`. Without that,
 * content dictates layout: a 19-digit Nuvei MID or a merchant called
 * THEORGANICSUPPLEMENTS stretched its column and squeezed the rest, and short
 * columns wrapped instead — "Added 22d ago" broke across two lines, so row
 * heights jumped and the table stopped scanning cleanly.
 *
 * So cells truncate with an ellipsis rather than wrapping, and hover reveals the
 * full value via `title`. Truncation is DISPLAY ONLY — toCsv reads exportValue
 * off the same column model, so an export always carries the complete string.
 */

/**
 * @param {Array} columns  [{ key, header, cell(row), className, headerClassName, align }]
 *   Falsy entries are dropped, so a page can inline `cond && {...}` for gating.
 *   `cell(row, index, total)` returns a node; omit it to render row[key]. The
 *   index is there for cells that must know they are near the bottom — the
 *   /tickets dependency popover opens upward on the last rows so the scroll
 *   container cannot clip it.
 * @param {Array} rows
 * @param {Function} rowKey  row => stable key
 * @param {Function} [rowClassName]  row => extra classes for the <tr>, for
 *   state that colours the whole row (e.g. a blocked ticket dimmed to
 *   bg-muted/30). A column may also declare `cellClassName(row)` for a per-row
 *   accent that must sit at the CELL edge — a left rail reads as a margin
 *   marker there, but inside the cell it just indents the content.
 * @param {Function} [onRowClick]  row => void. OMIT for read-only lists — rows
 *   then render non-interactive (no pointer, no tabstop, no Enter handler),
 *   which is what processor users get on /mids.
 * @param {boolean} loading
 * @param {any} [error]           truthy renders the error state
 * @param {Function} [onRetry]
 * @param {boolean} [filtered]    are filters active? picks the empty variant
 * @param {object} empty          { icon, heading, description, action }
 * @param {object} [noMatch]      { icon, heading, description }
 * @param {number} [skeletonRows]
 */
function titleFor(col, row) {
  if (col.title) return col.title(row);
  const value = col.exportValue ? col.exportValue(row) : row[col.key];
  return typeof value === "string" || typeof value === "number"
    ? String(value)
    : undefined;
}

export default function RecordTable({
  columns = [],
  rows = [],
  rowKey,
  rowClassName,
  onRowClick,
  loading = false,
  error = null,
  onRetry,
  filtered = false,
  empty = {},
  noMatch = {},
  skeletonRows = 8,
  sort,
  onSortChange,
  className = "",
}) {
  const cols = columns.filter(Boolean);
  const interactive = typeof onRowClick === "function";

  if (loading) {
    return <TableSkeleton cols={cols} rows={skeletonRows} className={className} />;
  }

  if (error) {
    return (
      <div className={`surface p-8 text-center ${className}`}>
        <p className="text-destructive">
          {empty.errorText || "Failed to load. The list may be incomplete."}
        </p>
        {onRetry && (
          <button className="btn btn-ghost mt-3" onClick={onRetry}>
            Retry
          </button>
        )}
      </div>
    );
  }

  if (!rows.length) {
    if (filtered) {
      const Icon = noMatch.icon || empty.icon;
      return (
        <div className={`surface py-12 text-center ${className}`}>
          {Icon && <Icon className="mx-auto h-10 w-10 text-muted-foreground" />}
          <h3 className="mt-2 text-sm font-medium text-foreground">
            {noMatch.heading || "No matching results"}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {noMatch.description || "Adjust your search or filters."}
          </p>
        </div>
      );
    }
    return (
      <EmptyState
        icon={empty.icon}
        heading={empty.heading}
        description={empty.description}
        action={empty.action}
        className={className}
      />
    );
  }

  return (
    <div className={`rounded-2xl border border-border bg-card shadow ${className}`}>
      <div className="overflow-x-auto">
        <table className="w-full table-fixed">
          <thead className="bg-card">
            <tr>
              {cols.map((col) => (
                <Th
                  key={col.key}
                  className={col.headerClassName}
                  align={col.align}
                  col={col}
                  sort={sort}
                  onSortChange={onSortChange}
                />
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((row, idx) => {
              const key = rowKey ? rowKey(row, idx) : idx;
              return (
                <tr
                  key={key}
                  tabIndex={interactive ? 0 : undefined}
                  onClick={interactive ? () => onRowClick(row) : undefined}
                  onKeyDown={
                    interactive
                      ? (e) => {
                          if (e.key === "Enter") onRowClick(row);
                        }
                      : undefined
                  }
                  className={`${
                    interactive
                      ? "cursor-pointer transition-colors hover:bg-muted focus:bg-muted focus:outline-none"
                      : "transition-colors"
                  } ${rowClassName ? rowClassName(row, idx) || "" : ""}`}
                >
                  {cols.map((col) => (
                    <td
                      key={col.key}
                      title={titleFor(col, row)}
                      className={`${cellClass(col)} ${
                        col.align === "right" ? "text-right" : ""
                      } ${col.className || ""} ${
                        col.cellClassName ? col.cellClassName(row) || "" : ""
                      }`}
                    >
                      {col.cell ? col.cell(row, idx, rows.length) : row[col.key]}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Skeleton mirrors the real column count so the table doesn't reflow on load.
 * The pages this replaces skeletoned their KPI strip but not the table, so the
 * shell appeared instantly and empty — reading as "no results" until data
 * arrived.
 */
function TableSkeleton({ cols, rows, className }) {
  return (
    <div className={`rounded-2xl border border-border bg-card shadow ${className}`}>
      <div className="overflow-x-auto">
        <table className="w-full table-fixed">
          <thead className="bg-card">
            <tr>
              {cols.map((col) => (
                <Th key={col.key} className={col.headerClassName} align={col.align} col={col} />
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {Array.from({ length: rows }).map((_, r) => (
              <tr key={r}>
                {cols.map((col) => (
                  <td key={col.key} className={CELL_BASE}>
                    <Skeleton.Rect height={12} width={r % 3 === 0 ? 120 : 88} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * A column is sortable if and only if it declares `sortKey`. Columns backed by
 * post-query enrichment (Client, DBA, Source) deliberately omit it, so they
 * render as plain labels with no affordance — the server cannot order by them,
 * and offering a control that silently does nothing is worse than offering none.
 */
export function Th({ children, className = "", align, col, sort, onSortChange }) {
  const base = `px-4 py-2 align-middle text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap ${
    align === "right" ? "text-right" : "text-left"
  } ${className}`;
  const label = children ?? col?.header;
  const style = col?.width ? { width: col.width } : undefined;

  if (!col?.sortKey || !onSortChange) {
    return (
      <th className={base} style={style}>
        {label}
      </th>
    );
  }

  const active = parseSort(sort);
  const isActive = active.field === col.sortKey;
  const Icon = !isActive ? ChevronsUpDown : active.dir === "asc" ? ChevronUp : ChevronDown;

  return (
    <th
      className={base}
      style={style}
      aria-sort={isActive ? (active.dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        onClick={() => onSortChange(toggleSort(sort, col))}
        className={`group inline-flex items-center gap-1 uppercase tracking-wider transition-colors hover:text-foreground ${
          align === "right" ? "flex-row-reverse" : ""
        } ${isActive ? "text-foreground" : ""}`}
      >
        {label}
        <Icon
          className={`h-3 w-3 shrink-0 transition-opacity ${
            isActive ? "opacity-100" : "opacity-0 group-hover:opacity-60"
          }`}
          aria-hidden="true"
        />
      </button>
    </th>
  );
}
